import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, ClickAwayListener, CircularProgress, InputBase, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { ErpResyncOptions, ErpSyncAuditResponse } from '../../../services/api';
import { useOrgFeatureFlag } from '../../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { tokens } from '../../../theme/tokens';
import { CameraWithPricing, OverrideFormState, parseOverrideTagsInput } from '../../../utils/productDetailHelpers';
import { getEffectiveMapForProduct, resolveDisplaySale } from '../../../utils/mapPricing';
import { pickMappedLiveListing } from '../../../utils/erpSyncLiveListing';
import { formatErpSyncCatalogLine } from '../../../utils/erpSyncAuditDisplay';
import { stripHtml } from '../../../utils/text';
import { ErpSyncFieldChanges } from '../../ErpSyncFieldChanges';
import { CopyProvenanceRow } from '../CopyProvenanceRow';
import { ResetShopifyLinkButton } from '../ResetShopifyLinkButton';
import { ResumeMotzuPricingButton } from '../ResumeMotzuPricingButton';
import { DateField } from '../../ui';
import { StoreSpecsEditor, type StoreSpecsEditorProps } from '../StoreSpecsEditor';
import { StoreAiCopyBlock, StoreAiSpecsBlock, type ProductOverridesAiCopy, type ProductOverridesAiSpecs } from '../ProductOverridesSection';
import {
  DealerCard,
  Dot,
  InfoBox,
  OutlineButton,
  PrimaryButton,
  SparkleGlyph,
  SyncGlyph,
  TextLink,
  ToggleRow,
  ToneNote,
  WarnBanner,
} from './primitives';
import { copyState, ListingModel, normalizeTag, sameList, storePriceEditTargets, usd, validateOverride } from './dealerModel';
import { StoreRelatedProductsEditor } from '../StoreRelatedProductsEditor';
import type { StoreRelatedPicksState } from '../useStoreRelatedPicks';
import { StorePreorderGoLiveEditor } from '../StorePreorderGoLiveEditor';
import type { StorePreorderGoLiveState } from '../useStorePreorderGoLive';
import type { RelatedPickItem } from '../../../types/relatedProducts';

const LISTING_DOT = {
  in_sync: tokens.signal.positive.solid,
  price_differs: tokens.signal.caution.solid,
  pending: tokens.ink.disabled,
  not_in_store: tokens.ink.disabled,
} as const;

const RESULT_TONE = {
  Pushed: tokens.signal.positive,
  Skipped: tokens.signal.caution,
  Failed: tokens.signal.problem,
} as const;

const inputSx = {
  width: '100%',
  boxSizing: 'border-box' as const,
  border: '1px solid',
  borderColor: tokens.line.default,
  borderRadius: '9px',
  fontSize: '13.5px',
  color: tokens.ink.primary,
  bgcolor: tokens.surface.card,
  '&.Mui-focused': { borderColor: tokens.accent.main, boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
};

const fieldLabelSx = { fontSize: '12px', fontWeight: 600, color: tokens.ink.bodyAlt } as const;

/** The shared DateField at the handoff's 36px input height. */
const dateFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': { height: 36, borderRadius: '9px', fontSize: '13.5px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.line.default },
} as const;

export interface DealerStoreTabProps {
  product: CameraWithPricing;
  audit: ErpSyncAuditResponse | undefined;
  listing: ListingModel;
  inSync: boolean;
  onAddToSync: () => void;
  addToSyncPending: boolean;
  onPush: (opts: ErpResyncOptions) => void;
  pushPending: boolean;
  onRemoveFromSync: () => void;
  removeFromSyncPending: boolean;
  onViewSyncSelection: () => void;

  form: OverrideFormState;
  savedForm: OverrideFormState;
  onFormChange: (updater: (prev: OverrideFormState) => OverrideFormState) => void;
  canManageStoreOverrides: boolean;
  overrideLoading: boolean;
  onSaveOverrides: () => Promise<unknown> | void;
  savePending: boolean;
  onGenerateDescription: () => void;
  generatePending: boolean;
  allowBelowMap: boolean;
  aiCopy?: ProductOverridesAiCopy | null;
  aiSpecs?: ProductOverridesAiSpecs | null;
  ownSpecs?: StoreSpecsEditorProps | null;
  /** The store's related and upsell picks (flag `retailer_related_products`); null while the flag is off. */
  relatedPicks?: StoreRelatedPicksState | null;
  onOpenRelatedProduct?: (item: RelatedPickItem) => void;
  /** The store's pre-order go-live (flag `store_preorder_go_live`, MOT-766); null while the flag is off. */
  preorderGoLive?: StorePreorderGoLiveState | null;
}

export const EMPTY_OVERRIDE: OverrideFormState = {
  overrideTitle: '',
  overrideDescription: '',
  overrideTags: '',
  overridePrice: '',
  overridePriceStartAt: '',
  overridePriceEndAt: '',
  ignoreSalePrice: false,
  ignoreRebate: false,
  keepRebate: false,
};

export const formsEqual = (a: OverrideFormState, b: OverrideFormState) =>
  (Object.keys(EMPTY_OVERRIDE) as Array<keyof OverrideFormState>).every((k) => a[k] === b[k]);

export function DealerStoreTab(props: DealerStoreTabProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', p: { xs: '16px', sm: '22px' }, bgcolor: tokens.surface.subtle }}>
      <StoreListingCard {...props} />
      <StoreOverridesCard {...props} />
      {props.preorderGoLive && <StorePreorderGoLiveEditor state={props.preorderGoLive} />}
      {props.relatedPicks && (
        <StoreRelatedProductsEditor state={props.relatedPicks} onOpenProduct={(item) => props.onOpenRelatedProduct?.(item)} />
      )}
    </Box>
  );
}

// ─── Store listing ───────────────────────────────────────────────────────────

function StoreListingCard({
  product,
  audit,
  listing,
  inSync,
  onAddToSync,
  addToSyncPending,
  onPush,
  pushPending,
  onRemoveFromSync,
  removeFromSyncPending,
  onViewSyncSelection,
}: DealerStoreTabProps) {
  const { t } = useAppTranslation();
  const { enabled: lightspeedConfigOn } = useOrgFeatureFlag('lightspeed_store_config');
  const { enabled: fieldHistoryOn } = useOrgFeatureFlag('sync_audit_field_history');
  const { enabled: copyProvenanceOn } = useOrgFeatureFlag('copy_provenance');
  const { enabled: respectStorePricesOn } = useOrgFeatureFlag('shopify_respect_store_prices');
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [historyAll, setHistoryAll] = useState(false);
  const [opts, setOpts] = useState<ErpResyncOptions>({});

  useEffect(() => {
    setOpts({});
    setHistoryAll(false);
  }, [product.id]);

  useEffect(() => {
    if (!menuOpen && !helpOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setHelpOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, helpOpen]);

  const { mapping } = pickMappedLiveListing(audit);
  const mappings = audit?.mappings ?? [];
  const lastPushed = mapping?.lastPushed;
  const linkOnly = mapping?.platform === 'shopify' && mapping.syncMode === 'link_only';
  const pricingOff = linkOnly && mapping?.syncPricing === false;
  const resetTargets = Array.from(
    new Map(
      mappings
        .filter((m) => m.platform === 'shopify' && m.integrationId)
        .map((m) => [
          m.integrationId,
          {
            integrationId: m.integrationId,
            shopDomain: m.shopDomain ?? audit?.live?.find((l) => l.integrationId === m.integrationId)?.shopDomain ?? null,
            blockedReason: m.syncProducts === false ? ('product_sync_off' as const) : m.inSyncSelection === false ? ('not_selected' as const) : null,
          },
        ]),
    ).values(),
  ).sort((a, b) => (a.shopDomain || a.integrationId).localeCompare(b.shopDomain || b.integrationId));

  // MOT-470: listings whose last push left the store's own price or promo alone.
  const resumeTargets = respectStorePricesOn ? storePriceEditTargets(audit) : [];

  const notInStore = listing.state === 'not_in_store' && !inSync;
  const pushLabel = notInStore ? (addToSyncPending ? 'Adding…' : 'Add to sync') : pushPending ? 'Pushing…' : 'Push now';
  const busy = notInStore ? addToSyncPending : pushPending;
  const doPush = () => {
    if (busy) return;
    setMenuOpen(false);
    if (notInStore) {
      onAddToSync();
      return;
    }
    onPush(opts);
    setOpts({});
  };

  const pushOptions: Array<{ key: keyof ErpResyncOptions; label: string; help: string }> = [
    { key: 'forceDescription', label: 'Update description', help: 'Overwrite your listing’s title, description and specs, even in enrich or link-only mode.' },
    { key: 'forceImages', label: 'Replace photos', help: `Delete the listing’s current photos and upload all ${product.images?.length ?? 0} from Motzu.` },
    { key: 'forcePricing', label: 'Push the price', help: 'Send this product’s price even when price syncing is off. Title and photos stay as they are.' },
    ...(lightspeedConfigOn
      ? [{ key: 'forceCategories' as const, label: t('integrations.lightspeedConfig.forceCategory.label'), help: t('integrations.lightspeedConfig.forceCategory.help') }]
      : []),
  ];

  const pushes = historyAll ? listing.pushes : listing.pushes.slice(0, 3);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: tokens.surface.card, border: '1px solid', borderColor: listing.state === 'price_differs' ? tokens.signal.caution.border : tokens.line.default, borderRadius: '12px' }}>
      {/* Head */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', px: '14px', py: '12px', bgcolor: tokens.surface.subtle, borderBottom: '1px solid', borderColor: tokens.line.hairline, borderRadius: '12px 12px 0 0', flexWrap: 'wrap' }}>
        <Dot color={LISTING_DOT[listing.state]} size={8} />
        <Box sx={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600 }}>{listing.title}</Typography>
            {listing.adminUrl && <TextLink href={listing.adminUrl} sx={{ fontSize: '12px' }}>Open listing</TextLink>}
          </Box>
          <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{listing.detail}</Typography>
        </Box>

        <ClickAwayListener onClickAway={() => setHelpOpen(false)}>
          <Box sx={{ position: 'relative', flex: '0 0 auto' }}>
            <ButtonBase
              onClick={() => { setHelpOpen((v) => !v); setMenuOpen(false); }}
              aria-label="How store sync works"
              aria-expanded={helpOpen}
              sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: '8px', bgcolor: helpOpen ? tokens.surface.tint : 'transparent', '&:hover': { bgcolor: tokens.surface.tint } }}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
                <circle cx="8" cy="8" r="6.2" stroke={tokens.ink.muted} strokeWidth="1.3" />
                <path d="M8 7.2v3.9M8 4.9v.4" stroke={tokens.ink.muted} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </ButtonBase>
            {helpOpen && (
              <Box role="dialog" sx={{ position: 'absolute', top: 36, right: 0, zIndex: 40, width: { xs: 280, sm: 340 }, px: '14px', py: '12px', bgcolor: tokens.surface.header, color: tokens.ink.onDark, borderRadius: '10px', boxShadow: '0 12px 32px rgba(15,23,42,0.24)', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px', lineHeight: 1.55 }}>
                <span>Motzu pushes catalog changes to your store listing on each sync. While a rebate is live, the sale price is MAP minus the rebate, and MAP shows as the compare-at price.</span>
                <span>Push now sends this product right away, even if Motzu thinks it is already up to date. It follows your sync mode.</span>
                <span>The options under Push now (update copy, replace photos, push the price) apply to that one push only. They aren't saved.</span>
              </Box>
            )}
          </Box>
        </ClickAwayListener>

        <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
          <Box sx={{ position: 'relative', flex: '0 0 auto', display: 'inline-flex', alignItems: 'stretch', height: 32, borderRadius: '9px', bgcolor: tokens.accent.main }}>
            <ButtonBase
              onClick={doPush}
              disabled={busy}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', height: 32, px: '12px', borderRadius: notInStore ? '9px' : '9px 0 0 9px', color: tokens.ink.inverse, fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { bgcolor: tokens.accent.hover } }}
            >
              {busy ? <CircularProgress size={12} sx={{ color: tokens.ink.inverse }} /> : <SyncGlyph />}
              <span>{pushLabel}</span>
            </ButtonBase>
            {!notInStore && (
              <ButtonBase
                onClick={() => { setMenuOpen((v) => !v); setHelpOpen(false); }}
                aria-label="Push options"
                aria-expanded={menuOpen}
                sx={{ display: 'grid', placeItems: 'center', width: 28, borderLeft: '1px solid rgba(255,255,255,0.28)', borderRadius: '0 9px 9px 0', '&:hover': { bgcolor: tokens.accent.hover } }}
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden><path d="M2.5 4.5L6 8l3.5-3.5" stroke={tokens.ink.inverse} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </ButtonBase>
            )}
            {menuOpen && (
              <Box role="menu" sx={{ position: 'absolute', top: 38, right: 0, zIndex: 40, width: 300, bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: '12px', boxShadow: '0 12px 32px rgba(15,23,42,0.14)', p: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography component="span" sx={{ px: '8px', pt: '4px', pb: '6px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>Also on the next push</Typography>
                {pushOptions.map((o) => {
                  const on = !!opts[o.key];
                  return (
                    <ButtonBase
                      key={o.key}
                      role="menuitemcheckbox"
                      aria-checked={on}
                      onClick={() => setOpts((prev) => ({ ...prev, [o.key]: !prev[o.key] }))}
                      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: '9px', width: '100%', textAlign: 'left', borderRadius: '8px', p: '8px', '&:hover': { bgcolor: tokens.surface.sunken } }}
                    >
                      <Box component="span" sx={{ flex: '0 0 auto', mt: '1px', width: 15, height: 15, borderRadius: '4px', border: '1px solid', borderColor: on ? tokens.accent.main : tokens.ink.disabled, bgcolor: on ? tokens.accent.main : tokens.surface.card, display: 'grid', placeItems: 'center' }}>
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden><path d="M1.5 5.2l2.2 2.2L8.5 2.6" stroke={tokens.ink.inverse} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </Box>
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 500, color: tokens.ink.primary }}>{o.label}</Typography>
                        <Typography component="span" sx={{ fontSize: '12px', lineHeight: 1.45, color: tokens.ink.muted }}>{o.help}</Typography>
                      </Box>
                    </ButtonBase>
                  );
                })}
                <Box sx={{ borderTop: '1px solid', borderColor: tokens.line.hairline, mt: '4px', pt: '4px', display: 'flex', flexDirection: 'column' }}>
                  <ButtonBase onClick={() => { setMenuOpen(false); onViewSyncSelection(); }} sx={{ justifyContent: 'flex-start', p: '8px', borderRadius: '8px', fontSize: '13px', color: tokens.ink.primary, '&:hover': { bgcolor: tokens.surface.sunken } }}>
                    View sync selection
                  </ButtonBase>
                  {inSync && (
                    <ButtonBase
                      onClick={() => { setMenuOpen(false); onRemoveFromSync(); }}
                      disabled={removeFromSyncPending}
                      sx={{ justifyContent: 'flex-start', p: '8px', borderRadius: '8px', fontSize: '13px', color: tokens.signal.problem.ink, '&:hover': { bgcolor: tokens.surface.sunken } }}
                    >
                      {removeFromSyncPending ? 'Removing…' : 'Remove from sync'}
                    </ButtonBase>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      </Box>

      {/* Notes the handoff has no slot for */}
      {(linkOnly || listing.sharedVariantNote || listing.liveError || resetTargets.length > 0 || resumeTargets.length > 0) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', px: '14px', pt: '12px' }}>
          {linkOnly && (
            pricingOff ? (
              <WarnBanner
                action={<OutlineButton height={30} onClick={() => onPush({ forcePricing: true })} disabled={pushPending}>Push the price</OutlineButton>}
              >
                Link only leaves titles, photos and listing copy as you set them, and price syncing is off, so a scheduled sync will not change this price.
              </WarnBanner>
            ) : (
              <InfoBox>Link only leaves titles, photos and listing copy as you set them. Sync pricing is on, so the matching variant's price updates on each sync.</InfoBox>
            )
          )}
          {listing.sharedVariantNote && <InfoBox>{listing.sharedVariantNote}</InfoBox>}
          {listing.liveError && <WarnBanner>{listing.liveError}</WarnBanner>}
          {resumeTargets.map((target) => (
            <WarnBanner
              key={`resume-${target.integrationId}`}
              action={
                <ResumeMotzuPricingButton
                  integrationId={target.integrationId}
                  productId={product.id}
                  shopDomain={target.shopDomain}
                  showShopInLabel={resumeTargets.length > 1}
                />
              }
            >
              {target.message}
            </WarnBanner>
          ))}
          {resetTargets.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {resetTargets.map((target) => (
                <ResetShopifyLinkButton
                  key={target.integrationId}
                  integrationId={target.integrationId}
                  productId={product.id}
                  shopDomain={target.shopDomain}
                  showShopInLabel={resetTargets.length > 1}
                  blockedReason={target.blockedReason}
                  disabled={pushPending}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Live vs catalog */}
      {listing.compare.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(auto-fit, minmax(150px, 1fr))' }, gap: '10px', p: '14px', borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
          {listing.compare.map((c) => (
            <Box key={c.label} sx={{ display: 'flex', flexDirection: 'column', gap: '3px', px: '11px', py: '9px', border: '1px solid', borderColor: c.warn ? tokens.signal.caution.border : tokens.line.hairline, borderRadius: '9px', bgcolor: c.warn ? tokens.signal.caution.row : tokens.surface.card }}>
              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>{c.label}</Typography>
              <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{c.live}</Typography>
              <Typography component="span" sx={{ fontSize: '11.5px', color: c.warn ? tokens.signal.caution.ink : tokens.ink.faint }}>{c.note || ' '}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Recent pushes */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '14px', pt: '10px', pb: '4px' }}>
          <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
            {historyAll ? 'Push history' : 'Recent pushes'}
          </Typography>
          {listing.pushes.length > 0 && (
            <TextLink onClick={() => setHistoryAll((v) => !v)} sx={{ fontSize: '12px' }}>{historyAll ? 'Show recent' : 'Full history'}</TextLink>
          )}
        </Box>
        {pushes.map((q) => {
          const tone = RESULT_TONE[q.result];
          return (
            <Box key={q.id} sx={{ px: '14px', py: '9px', borderTop: '1px solid', borderColor: tokens.surface.sunken }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '130px 72px minmax(0,1fr)' }, gap: '4px 12px', alignItems: 'baseline' }}>
                <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{q.when}</Typography>
                <Box component="span" sx={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: 20, px: '8px', borderRadius: '6px', bgcolor: tone.tint, color: tone.ink, fontSize: '11.5px', fontWeight: 600 }}>{q.result}</Box>
                <Typography component="span" sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, fontSize: '12.5px', lineHeight: 1.5, color: tokens.ink.body }}>{q.summary}</Typography>
              </Box>
              {historyAll && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: '6px', ml: { sm: '214px' } }}>
                  <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.muted }}>
                    Catalog at sync: {formatErpSyncCatalogLine(q.event.catalog, q.event.pushed, usd)}
                  </Typography>
                  {q.event.skipCode && (
                    <Typography component="span" sx={{ fontSize: '11.5px', fontFamily: tokens.font.mono, color: tokens.ink.muted }}>{q.event.skipCode}</Typography>
                  )}
                  {fieldHistoryOn && <ErpSyncFieldChanges changes={q.event.fieldChanges} />}
                </Box>
              )}
            </Box>
          );
        })}
        {historyAll && lastPushed && (
          <Typography sx={{ px: '14px', py: '8px', fontSize: '12px', color: tokens.ink.muted, borderTop: '1px solid', borderColor: tokens.surface.sunken }}>
            Last Motzu push: {usd(lastPushed.price)}{lastPushed.compareAtPrice ? ` (was ${usd(lastPushed.compareAtPrice)})` : ''}
          </Typography>
        )}
        {copyProvenanceOn && lastPushed?.copyProvenance && (
          <Box sx={{ px: '14px', py: '8px', borderTop: '1px solid', borderColor: tokens.surface.sunken }}>
            <CopyProvenanceRow provenance={lastPushed.copyProvenance} />
          </Box>
        )}
        {listing.pushes.length === 0 && (
          <Typography sx={{ px: '14px', pt: '6px', pb: '14px', fontSize: '12.5px', color: tokens.ink.faint }}>
            {notInStore ? 'Nothing has been pushed yet. Add to sync and the next run creates the listing.' : 'Nothing has been pushed yet. The next sync logs its push here.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Store overrides ─────────────────────────────────────────────────────────

function StoreOverridesCard({
  product,
  form,
  savedForm,
  onFormChange,
  canManageStoreOverrides,
  overrideLoading,
  onSaveOverrides,
  savePending,
  onGenerateDescription,
  generatePending,
  allowBelowMap,
  aiCopy,
  aiSpecs,
  ownSpecs,
}: DealerStoreTabProps) {
  const navigate = useNavigate();
  const [tagDraft, setTagDraft] = useState('');
  const tagInputRef = useRef<HTMLInputElement | null>(null);
  const { enabled: keepRebateEnabled } = useOrgFeatureFlag('override_keeps_rebate');
  const set = <K extends keyof OverrideFormState>(key: K, value: OverrideFormState[K]) => onFormChange((prev) => ({ ...prev, [key]: value }));

  const catalogTitle = (product.name || product.model || '').trim();
  const catalogDesc = stripHtml(product.description || '').trim();
  const catalogTags = product.tags ?? [];
  const dirty = !formsEqual(form, savedForm);
  const disabled = !canManageStoreOverrides || overrideLoading;

  // Tags: the working list is the override, else the catalog list. Matching the catalog clears the override.
  const currentTags = form.overrideTags ? parseOverrideTagsInput(form.overrideTags) : catalogTags;
  const setTags = (list: string[]) => set('overrideTags', sameList(list, catalogTags) ? '' : list.join(', '));
  const addTag = (raw: string) => {
    const v = normalizeTag(raw);
    setTagDraft('');
    if (v && !currentTags.includes(v)) setTags([...currentTags, v]);
  };

  const titleState = copyState(form.overrideTitle, savedForm.overrideTitle);
  const descState = copyState(form.overrideDescription, savedForm.overrideDescription);
  const tagState = copyState(form.overrideTags, savedForm.overrideTags);

  // Summary of what is saved.
  const parts: string[] = [];
  if (savedForm.overrideTitle) parts.push('Title');
  if (savedForm.overrideDescription) parts.push('Description');
  if (savedForm.overrideTags) parts.push('Tags');
  if (savedForm.overridePrice) parts.push(`Price $${savedForm.overridePrice.replace(/^\$/, '')}${savedForm.overridePriceEndAt ? ` until ${savedForm.overridePriceEndAt}` : ''}`);
  if (savedForm.ignoreSalePrice) parts.push('Ignoring sale');
  if (savedForm.ignoreRebate) parts.push('Ignoring rebate');
  if (savedForm.keepRebate) parts.push('Keeping rebates');

  // Price override note.
  const em = getEffectiveMapForProduct(product);
  const sale = resolveDisplaySale(product, product.pricingData);
  const hasSale = sale.isSaleActive;
  const { floor, hasRebate, priceInvalid, belowFloor, floorBlocking, dateRangeInvalid, blocked: saveBlocked } = validateOverride(product, form, allowBelowMap);
  const floorLabel = hasRebate ? 'MAP after rebate' : 'MAP';
  const standard = sale.sellingPrice > 0 ? sale.sellingPrice : product.price > 0 ? product.price : null;
  const priceText = form.overridePrice.trim();

  let note: { tone: 'neutral' | 'caution' | 'positive'; text: React.ReactNode };
  if (priceInvalid) note = { tone: 'caution', text: 'Enter a price above zero. A zero or negative override would be pushed to your storefront as-is.' };
  else if (form.ignoreRebate && hasRebate) note = { tone: 'neutral', text: `Pushes ${usd(em?.raw)} with no compare-at while the rebate runs. Shoppers won't see the rebate on your store.` };
  else if (keepRebateEnabled && form.keepRebate && hasRebate && !belowFloor && priceText) {
    const yourPrice = Number(priceText.replace(/^\$/, ''));
    const afterRebate = Math.max(yourPrice - (em?.instantRebate ?? 0), floor ?? 0);
    note = { tone: 'positive', text: `Sells at ${usd(afterRebate)} while the rebate runs, with ${usd(yourPrice)} as the compare-at. Sells at ${usd(yourPrice)} after it ends.` };
  } else if (belowFloor) {
    note = {
      tone: 'caution',
      text: floorBlocking ? (
        <>
          Below the {usd(floor)} {floorLabel}. Your store does not allow prices below MAP, so this can't be saved. To allow it, turn it on in{' '}
          <Box component="span" role="link" tabIndex={0} onClick={() => navigate('/app/pricing-preferences')} sx={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Pricing Preferences</Box>.
        </>
      ) : (
        `Below the ${usd(floor)} ${floorLabel}. ${product.brand} can flag listings advertised under MAP.`
      ),
    };
  } else if (floor != null && priceText) note = { tone: 'positive', text: `At or above the ${usd(floor)} ${floorLabel}.` };
  else if (floor != null) note = { tone: 'neutral', text: `Leave blank to use the standard price (${usd(standard)}). Don't advertise below ${usd(floor)} while MAP applies.` };
  else note = { tone: 'neutral', text: `Leave blank to use the standard price (${usd(standard)}). No MAP on this product, so any price is allowed.` };

  const statusLine = (state: { label: string; edited: boolean }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: state.edited ? tokens.accent.hover : tokens.ink.muted }}>
      <Dot color={state.edited ? tokens.accent.main : tokens.ink.disabled} />
      {state.label}
    </Box>
  );

  return (
    <DealerCard
      title={
        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <span>Store overrides</span>
          <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 400, color: tokens.ink.muted }}>
            {parts.length ? parts.join(' · ') : 'Nothing overridden. Catalog values sync as-is.'}
          </Typography>
        </Box>
      }
      actions={parts.length > 0 ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: '8px', borderRadius: '6px', bgcolor: tokens.accent.tint, color: tokens.accent.hover, fontSize: '11.5px', fontWeight: 600 }}>{parts.length}</Box>
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', px: '18px', py: '16px' }}>
        <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: tokens.ink.muted }}>
          This is what your store listing shows. Edit the title or description to override the catalog copy; revert any time to go back to syncing it.
        </Typography>
        {!canManageStoreOverrides && (
          <InfoBox>Only an owner or admin of this store can change its overrides.</InfoBox>
        )}

        {aiCopy && <StoreAiCopyBlock aiCopy={aiCopy} canManageStoreOverrides={canManageStoreOverrides} overrideLoading={overrideLoading} />}

        {/* Title */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', minHeight: 26, flexWrap: 'wrap' }}>
            <Typography component="label" htmlFor="dealer-ov-title" sx={fieldLabelSx}>Title</Typography>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {statusLine(titleState)}
              {titleState.edited && !disabled && <TextLink onClick={() => set('overrideTitle', '')} sx={{ fontSize: '12px' }}>Revert to catalog</TextLink>}
            </Box>
          </Box>
          <InputBase
            id="dealer-ov-title"
            value={form.overrideTitle || catalogTitle}
            onChange={(e) => set('overrideTitle', e.target.value.trim() === catalogTitle ? '' : e.target.value)}
            disabled={disabled}
            sx={{ ...inputSx, height: 36, px: '11px' }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Typography component="label" htmlFor="dealer-ov-desc" sx={fieldLabelSx}>Description</Typography>
              {statusLine(descState)}
              {descState.edited && !disabled && <TextLink onClick={() => set('overrideDescription', '')} sx={{ fontSize: '12px' }}>Revert to catalog</TextLink>}
            </Box>
            <OutlineButton
              height={26}
              onClick={onGenerateDescription}
              disabled={disabled || generatePending}
              startIcon={generatePending ? <CircularProgress size={10} /> : <SparkleGlyph />}
              sx={{ px: '9px', borderRadius: '7px', fontSize: '12px', color: tokens.accent.hover, '&:hover': { borderColor: tokens.accent.border, bgcolor: tokens.signal.info.row } }}
            >
              {generatePending ? 'Writing…' : 'Generate with AI'}
            </OutlineButton>
          </Box>
          <InputBase
            id="dealer-ov-desc"
            multiline
            minRows={6}
            value={form.overrideDescription || catalogDesc}
            onChange={(e) => set('overrideDescription', e.target.value.trim() === catalogDesc ? '' : e.target.value)}
            disabled={disabled}
            placeholder="Catalog description"
            sx={{ ...inputSx, px: '11px', py: '9px', lineHeight: 1.55, alignItems: 'flex-start' }}
          />
        </Box>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', minHeight: 26, flexWrap: 'wrap' }}>
            <Typography component="label" htmlFor="dealer-ov-tags" sx={fieldLabelSx}>
              Tags <Box component="span" sx={{ fontWeight: 500, color: tokens.ink.faint }}>{currentTags.length}</Box>
            </Typography>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {statusLine(tagState)}
              {tagState.edited && !disabled && <TextLink onClick={() => set('overrideTags', '')} sx={{ fontSize: '12px' }}>Revert to catalog</TextLink>}
            </Box>
          </Box>
          <Box onClick={() => tagInputRef.current?.focus()} sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', p: '7px', border: '1px solid', borderColor: tokens.line.default, borderRadius: '9px', bgcolor: tokens.surface.card, cursor: 'text' }}>
            {currentTags.map((tag) => {
              const added = !catalogTags.includes(tag);
              return (
                <Box key={tag} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', height: 24, pl: '8px', pr: '4px', borderRadius: '7px', bgcolor: added ? tokens.accent.tint : tokens.surface.tint, color: added ? tokens.accent.hover : tokens.ink.body, fontSize: '12px' }}>
                  <span>{tag}</span>
                  {!disabled && (
                    <ButtonBase
                      aria-label={`Remove tag ${tag}`}
                      onClick={(e) => { e.stopPropagation(); setTags(currentTags.filter((x) => x !== tag)); }}
                      sx={{ display: 'grid', placeItems: 'center', width: 16, height: 16, borderRadius: '4px', color: tokens.ink.faint, '&:hover': { color: tokens.signal.problem.ink, bgcolor: tokens.surface.card } }}
                    >
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none" aria-hidden><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
                    </ButtonBase>
                  )}
                </Box>
              );
            })}
            <InputBase
              id="dealer-ov-tags"
              inputRef={tagInputRef}
              value={tagDraft}
              disabled={disabled}
              placeholder="Add tag"
              onChange={(e) => {
                const v = e.target.value;
                if (v.endsWith(',')) addTag(v.slice(0, -1));
                else setTagDraft(v);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(tagDraft); }
                else if (e.key === 'Backspace' && !tagDraft && currentTags.length) setTags(currentTags.slice(0, -1));
              }}
              onBlur={() => tagDraft && addTag(tagDraft)}
              sx={{ flex: '1 1 120px', minWidth: 100, height: 24, fontSize: '12.5px' }}
            />
          </Box>
          <Typography sx={{ fontSize: '11.5px', lineHeight: 1.5, color: tokens.ink.muted }}>
            Press Enter or comma to add. Tags you add are shown in blue. Motzu always adds motzu_synced.
          </Typography>
        </Box>

        {aiSpecs && <StoreAiSpecsBlock aiSpecs={aiSpecs} canManageStoreOverrides={canManageStoreOverrides} />}
        {ownSpecs && <StoreSpecsEditor {...ownSpecs} disabled={ownSpecs.disabled || !canManageStoreOverrides} />}

        {/* Price override */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', pt: '14px', borderTop: '1px solid', borderColor: tokens.line.hairline }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>Price override</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(auto-fit, minmax(160px, 1fr))' }, gap: '10px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography component="label" htmlFor="dealer-ov-price" sx={fieldLabelSx}>Price</Typography>
              <InputBase
                id="dealer-ov-price"
                value={form.overridePrice}
                onChange={(e) => set('overridePrice', e.target.value.replace(/[^0-9.]/g, ''))}
                disabled={disabled}
                placeholder={standard != null ? usd(standard) : ''}
                inputProps={{ inputMode: 'decimal' }}
                sx={{ ...inputSx, height: 36, px: '11px', fontVariantNumeric: 'tabular-nums', borderColor: belowFloor || priceInvalid ? tokens.signal.caution.solid : tokens.line.default }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography component="label" sx={fieldLabelSx}>Starts</Typography>
              <DateField aria-label="Starts" value={form.overridePriceStartAt} onChange={(v) => set('overridePriceStartAt', v)} disabled={disabled} sx={dateFieldSx} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography component="label" sx={fieldLabelSx}>Ends</Typography>
              <DateField aria-label="Ends" value={form.overridePriceEndAt} onChange={(v) => set('overridePriceEndAt', v)} disabled={disabled} error={dateRangeInvalid} sx={dateFieldSx} />
            </Box>
          </Box>
          <ToneNote tone={note.tone}>{note.text}</ToneNote>
          {dateRangeInvalid && <ToneNote tone="caution">The end date is before the start date. An override with that window can never become active.</ToneNote>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <ToggleRow
              on={form.ignoreSalePrice}
              label="Ignore sale price"
              help={hasSale || form.ignoreSalePrice ? 'Push retail even while a sale is active.' : 'No sale running on this product'}
              disabled={disabled || (!hasSale && !form.ignoreSalePrice)}
              onToggle={() => set('ignoreSalePrice', !form.ignoreSalePrice)}
            />
            <ToggleRow
              on={form.ignoreRebate}
              label="Ignore rebate"
              help={hasRebate || form.ignoreRebate ? 'Push the pre-rebate price and skip the compare-at swap.' : 'No rebate running on this product'}
              disabled={disabled || (!hasRebate && !form.ignoreRebate)}
              onToggle={() => {
                if (!form.ignoreRebate && form.keepRebate) set('keepRebate', false);
                set('ignoreRebate', !form.ignoreRebate);
              }}
            />
            {keepRebateEnabled && (
              <ToggleRow
                on={form.keepRebate}
                label="Keep rebates on my price"
                help={form.overridePrice.trim() || form.keepRebate ? 'While a rebate runs, sell at your price minus the rebate. Otherwise sell at your price.' : 'Set a price override first'}
                disabled={disabled || (!form.overridePrice.trim() && !form.keepRebate)}
                onToggle={() => {
                  if (!form.keepRebate && form.ignoreRebate) set('ignoreRebate', false);
                  set('keepRebate', !form.keepRebate);
                }}
              />
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: '12px', borderTop: '1px solid', borderColor: tokens.line.hairline, flexWrap: 'wrap' }}>
          <PrimaryButton onClick={() => onSaveOverrides()} disabled={!dirty || disabled || savePending || saveBlocked}>
            {savePending ? 'Saving…' : 'Save overrides'}
          </PrimaryButton>
          <OutlineButton height={34} onClick={() => onFormChange(() => EMPTY_OVERRIDE)} disabled={disabled || formsEqual(form, EMPTY_OVERRIDE)} sx={{ fontSize: '13px', color: tokens.ink.bodyAlt }}>
            Clear all
          </OutlineButton>
          <Box sx={{ flex: '1 1 auto' }} />
          {dirty && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: tokens.signal.caution.ink }}>
              <Dot color={tokens.signal.caution.solid} />Unsaved changes
            </Box>
          )}
          <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>Saved overrides go out on the next push.</Typography>
        </Box>
      </Box>
    </DealerCard>
  );
}
